import { CdkDragDrop, CdkDragSortEvent } from '@angular/cdk/drag-drop';
import { Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  CdkVirtualScrollable,
  CdkVirtualScrollViewport,
  VIRTUAL_SCROLLABLE,
} from '@angular/cdk/scrolling';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import {
  afterNextRender,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  inject,
  Injector,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  output,
  Output,
  QueryList,
  signal,
  TrackByFunction,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { MatDialog, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { produce } from 'immer';
import {
  animationFrameScheduler,
  BehaviorSubject,
  buffer,
  catchError,
  combineLatest,
  concatMap,
  debounce,
  debounceTime,
  defer,
  distinctUntilChanged,
  EMPTY,
  exhaustMap,
  filter,
  finalize,
  first,
  fromEvent,
  iif,
  map,
  merge,
  mergeMap,
  MonoTypeOperatorFunction,
  Observable,
  observeOn,
  of,
  repeat,
  skip,
  Subject,
  Subscription,
  switchMap,
  take,
  takeUntil,
  tap,
  throwError,
  timer,
  withLatestFrom,
} from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import {
  BlazeVirtualScrollableElementDirective,
  RowAutosizeVirtualScrollDirective,
  VIRTUAL_SCROLL_ITEM_SIZE_PREDICTOR,
  VIRTUAL_SCROLL_NODES_PROVIDER,
  VirtualScrollableRegistryService,
} from '@blaze-runtime/engine-ui-feature';
import {
  ColumnDataType,
  ColumnId,
  ComparisonOperator,
  ComparisonQueryNode,
  EnvironmentName,
  FailureResponse,
  FormatType,
  JobInfo,
  LogicalPredicateNode,
  LogicalQueryNode,
  LogicalType,
  PaginatedGetResponse,
  PredicateType,
  ReferenceInfo,
  SortRequest,
  SystemTableType,
  TableInfo,
} from '@blaze-shared/api-contracts-domain';
import { blazeActions, fromRouter } from '@blaze-shared/app-state-data-access';
import {
  DatasetsApiService,
  TablesApiService,
  TablesCollectionService,
} from '@blaze-shared/datasets-data-access';
import {
  BlazeTable,
  BlazeTableAction,
  BlazeTableActionType,
  BlazeTableValidationError,
  convertBlazeTableSchemaToBlazeTable,
} from '@blaze-shared/datasets-domain';
import { generateErrorMessageFromResponse, JobsFacade } from '@blaze-shared/jobs-data-access';
import { NotificationService } from '@blaze-shared/platform-data-access';
import { SchemaService } from '@blaze-shared/schema-data-access';
import {
  BlazeTableSchema,
  ColumnQueryLike,
  ColumnType,
  convertDatasetColumnsToColumnInfos,
  DatasetColumn,
  DatasetRow,
  findColumn,
  findColumnIndex,
  findColumnKey,
  formatLinkedInlineColumnFormula,
  getColumnKey,
  hasNoneDefaultValue,
  isLinkedInlineColumn,
} from '@blaze-shared/schema-domain';
import {
  DialogService,
  moveItemInArrayImmutably,
  ProgressDialogComponent,
  ProgressDialogInput,
} from '@blaze-shared/shared-feature';
import { BlazeCheckboxChange, lifecycleRxUtils, safeDetectChanges } from '@blaze-shared/shared-ui';
import {
  deepClone,
  insertItemAt,
  intersectionRxUtils,
  isDeepEqual,
  isEqual,
  isNullish,
  resizeRxUtils,
  runAfterFramePaint,
} from '@blaze-shared/shared-util';

import { CellInputComponent } from '../cell-input/cell-input.component';
import { ColumnEditorPanelComponent } from '../column-editor-panel/column-editor-panel.component';
import { ColumnUseDialogComponent } from '../column-references/column-use-dialog/column-use-dialog.component';
import { BlazeTableLoggerService } from '../shared/blaze-table-logger.service';
import {
  CellEditInfo,
  DatasetColumnExtendedForLinkEdit,
  DatasetLinkExtendedForLinkEdit,
  ErrorFields,
  RowUpdateInfo,
} from '../shared/blaze-table.model';
import { BlazeTableService, BlazeTableValue } from '../shared/blaze-table.service';
import { BlazeTableStore } from '../shared/blaze-table.store';
import {
  destroyOverlayRef,
  extractRowsWithErrors,
  generatePositionStrategyForOverlay,
} from '../shared/blaze-table.utils';
import {
  checkIfLinkedColumnsNotChanged,
  getColumnsToAdd,
  getColumnsToRemove,
  getLinkedColumnMatch,
} from '../shared/utils/column-type-link.utils';

import { ExtraStartAction } from './blaze-table.model';
import { BlazeTableItemHeightPredictor, BlazeTableRowsProvider } from './virtual-scroll.providers';

const NEW_ROW_INDEX = -1;
const STICKY_COLUMNS_COUNT = 1;
const CELL_WIDTH_PX = 240;
const FIRST_CELL_FROM_LAST_ROW_SELECTOR =
  '.table-row.add-row:last-of-type:not(.table-row-header) td:first-of-type';

const COLUMN_TO_TEMPLATE_MAP = {
  File: 'fileTpl',
  Duration: 'durationTpl',
  Boolean: 'booleanTpl',
  Number: 'numberTpl',
  Currency: 'currencyTpl',
  Percent: 'percentageTpl',
  SecureText: 'secureTpl',
  CreatedTime: 'datetimeTpl',
  DateTime: 'datetimeTpl',
  Date: 'dateTpl',
  Time: 'timeTpl',
  GeoPoint: 'geopointTpl',
  Link: 'linkTpl',
  Tag: 'tagTpl',
  Image: 'imageTpl',
  PhoneNumber: 'phoneNumberTpl',
  Signature: 'signatureTpl',
  LastUpdatedTime: 'datetimeTpl',
  RichText: 'richTextTpl',
  TimeZone: 'timezoneTpl',
} as Record<Partial<ColumnType | ColumnDataType | FormatType>, string>;

@UntilDestroy()
@Component({
  selector: 'blaze-table',
  templateUrl: './blaze-table.component.html',
  styleUrls: ['./blaze-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    VirtualScrollableRegistryService,
    {
      provide: VIRTUAL_SCROLL_ITEM_SIZE_PREDICTOR,
      useClass: BlazeTableItemHeightPredictor,
    },
    {
      provide: VIRTUAL_SCROLL_NODES_PROVIDER,
      useClass: BlazeTableRowsProvider,
    },
    {
      provide: VIRTUAL_SCROLLABLE,
      useFactory: (registry: VirtualScrollableRegistryService): CdkVirtualScrollable | null =>
        registry.getInstance(),
      deps: [VirtualScrollableRegistryService],
    },
  ],
  standalone: false,
})
export class BlazeTableComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly datasetsApiService = inject(DatasetsApiService);
  private readonly dialog = inject(MatDialog);
  private readonly dialogService = inject(DialogService);
  private readonly injector = inject(Injector);
  private readonly jobsFacade = inject(JobsFacade);
  private readonly logger = inject(BlazeTableLoggerService);
  private readonly ngZone = inject(NgZone);
  private readonly notificationService = inject(NotificationService);
  private readonly overlay = inject(Overlay);
  private readonly schemaService = inject(SchemaService);
  private readonly scrollableRegistry = inject(VirtualScrollableRegistryService);
  private readonly store = inject(Store);
  private readonly tablesApiService = inject(TablesApiService);
  private readonly tablesCollService = inject(TablesCollectionService);
  readonly blazeTableService = inject(BlazeTableService);
  readonly blazeTableStore = inject(BlazeTableStore);
  readonly elRef = inject(ElementRef);

  refreshDataset = output<void>();

  @Input() table$!: Observable<{
    config: BlazeTable;
    data: DatasetRow[];
    pageToken: string | null;
    totalCount: number | null;
    filters?: LogicalQueryNode | ComparisonQueryNode;
  }>;
  @Input() parentRef!: ElementRef;
  @Input() projectId: string;

  @Output() configEmitter = new EventEmitter<{ config?: BlazeTable }>();

  @ViewChild('table') table: ElementRef<HTMLTableElement> | null = null;
  @ViewChild(BlazeVirtualScrollableElementDirective) scrollable: CdkVirtualScrollable | null = null;
  @ViewChild(BlazeVirtualScrollableElementDirective, { read: ElementRef })
  scrollableElement: ElementRef<HTMLElement> | null = null;
  @ViewChild('rowVirtualScrollViewport') virtualScroll: CdkVirtualScrollViewport | null = null;
  @ViewChild(RowAutosizeVirtualScrollDirective)
  virtualScrollStrategy: RowAutosizeVirtualScrollDirective<any> | null = null;
  @ViewChild('addRowTpl') addRowTpl: ElementRef<HTMLElement | null> | null = null;

  @ViewChildren('addColumnTrigger') addColumnTrigger!: QueryList<ElementRef<HTMLElement>>;
  @ViewChildren('columnName') columnNames!: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChildren('rowSelect') rowSelectCheckboxes!: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChildren('cell') cells: QueryList<ElementRef<HTMLElement>> | null = null;
  @ViewChildren(CdkVirtualScrollViewport)
  virtualScrollViewports!: QueryList<CdkVirtualScrollViewport>;
  @ViewChildren('columnHeaders') columnHeaders!: QueryList<ElementRef<HTMLTableCellElement>>;
  @ViewChildren('tableRow') tableRows: QueryList<ElementRef<HTMLElement>> | null = null;

  @HostBinding('style.--cell-width') cellWidth = `${CELL_WIDTH_PX}px`;

  private preventChangingProgressDialogData = false;
  private progressDialog?: MatDialogRef<ProgressDialogComponent> = undefined;
  private progressDialogClosed$ = new Observable();
  private progressDialogClosedSubscription?: Subscription;
  private viewRefresh$$ = new BehaviorSubject<any>(true);
  /**
   * Subject to trigger data fetching for the table.
   * Emits a boolean value indicating whether to fetch more data (pagination).
   * When true, it fetches the next page of data; when false, it fetches the first page.
   */
  private fetchDataTrigger$$ = new Subject<boolean>();
  /** subject that inform when data were updated */
  private dataUpdated$$ = new Subject<PaginatedGetResponse | undefined>();
  private shouldAllowConvertIntoAccessRole$ = new Subject<boolean>();
  /** Type of action that needs to be executed  */
  private columnActionType!: BlazeTableActionType;
  private pageToken!: string | undefined | null;
  private sort!: SortRequest;
  /** On which column action is being executed */
  private sourceColumn: DatasetColumn | null = null;
  private updateRow$ = new Subject<RowUpdateInfo>();
  private pageTokenCache = new Set<string>();
  private readonly filters = signal<LogicalQueryNode | undefined>(undefined);
  private valuePatchStop$ = new Subject();
  private recheckViewportSize = false;
  private refreshSchemaAfterClose = false;
  private nextPageFetchingSubscription?: Subscription;
  private componentDestroy$ = new Subject();
  private readonly rowSizeChanged$ = new Subject<void>();

  actionsInProgress$: Observable<boolean>;

  isVirtualScrollRegistered = false;
  /** Some columns are restricted. This is a list of columns which can be showed. */
  columns: DatasetColumn[] = [];
  columnReferences: Record<ColumnId, ReferenceInfo[]> = {};

  deletableColumnsCount = 0;
  config$ = this.blazeTableService.config$;
  columnEditorVisible$ = new BehaviorSubject<boolean>(false);
  /** {'row_id': {'column_name': 'Error message' }} */
  errorFields: ErrorFields = {};
  /** Rows used to render table view
   * We keep separate rows for view and for data in order to allow users use of TAB key to navigate between inputs.
   * Having same data structure for both causes view to be refreshed and TAB navigation stops working properly. */
  rows: (DatasetRow | undefined)[] = [];
  mouseOverCol: DatasetColumn | null = null;
  mouseOverRowIndex?: number;
  rowFocusIndex$$ = new BehaviorSubject<number | undefined>(undefined);
  rowFocusIndex?: number;

  selectedRows: Record<string | number, any> = {};
  hoveredRowCheck: number | undefined;

  totalCount!: number;
  rowsToUpdate: RowUpdateInfo[] = [];
  newRow: DatasetRow | undefined;

  isDragging = false;
  draggedColumn: DatasetColumn | null = null;
  draggedColumnsPreview: DatasetColumn[] | null = null;
  dragPreviewHeight = 0;

  progressDialogConfig$$ = new BehaviorSubject<ProgressDialogInput | undefined>(undefined);
  taskFetchPolingTrigger$ = new Subject();

  allowedConversions: Partial<Record<ColumnType, ColumnType[]>> = {};
  cellHeights: Record<string, number> = {};
  isLoading = false;
  cellWidthPx = CELL_WIDTH_PX;
  footerRowWidth: number | undefined;

  columnsTemplates: Record<string, string> = {};
  siblingColsForActiveCell: {
    prev?: { column: string; rowIndex: number };
    next?: { column: string; rowIndex: number };
  } = { prev: undefined, next: undefined };

  columnTrackByFn: TrackByFunction<DatasetColumn> = (index: number, column: DatasetColumn) => {
    return getColumnKey(column);
  };
  rowTrackByFn: TrackByFunction<DatasetColumn> = (index: number, row?: DatasetRow) => {
    return row?._id ?? index;
  };

  cellEdit$$ = new BehaviorSubject<CellEditInfo | undefined>(undefined);

  get config(): BlazeTable {
    return this.blazeTableService.config;
  }

  get virtualScrollVisibleColumns(): DatasetColumn[] {
    const cols = this.draggedColumnsPreview || this.columns;

    if (!cols?.length) {
      return [];
    }

    return cols.slice(STICKY_COLUMNS_COUNT);
  }

  get stickyColumns(): DatasetColumn[] {
    const cols = this.draggedColumnsPreview || this.columns;

    if (!cols?.length) {
      return [];
    }

    return cols.slice(0, STICKY_COLUMNS_COUNT);
  }

  get isDraggable(): boolean {
    return this.columns?.length > 1;
  }

  get hasMoreRows(): boolean {
    return this.rows.length < this.totalCount;
  }

  get isRowManagementEnabled(): boolean {
    return !!(
      !this.config.isChangeTrackingTable &&
      (!this.config.systemTableType ||
        this.config.systemTableType === SystemTableType.InternalUsers ||
        this.config.systemTableType === SystemTableType.Users ||
        this.config.systemTableType === SystemTableType.Accounts)
    );
  }

  get isReadOnly(): boolean {
    return (
      this.config.isChangeTrackingTable ||
      this.config.systemTableType === SystemTableType.UserActivityLog
    );
  }

  constructor() {
    const tablesApiService = this.tablesApiService;
    this.initializeActions();

    this.viewRefresh$$.pipe(debounceTime(100)).subscribe(() => {
      safeDetectChanges(this.cdr);

      if (this.recheckViewportSize) {
        this.recheckViewportSize = false;
        this.updateVirtualScrollViewportSize();
      }
    });
    // Emits after the component gets destroyed and no actions are pending
    const destroyedNoActionsPending$ = combineLatest({
      componentDestroyed: lifecycleRxUtils.emitOnDestroy(this, true),
      actionsInProgress: this.blazeTableStore.actionsInProgress$,
    }).pipe(
      filter(
        ({ componentDestroyed, actionsInProgress }) =>
          componentDestroyed && actionsInProgress === 0,
      ),
      map(() => true),
    );

    this.fetchDataTrigger$$
      .pipe(
        // to fetch all values from fetchDataTrigger over time and react accordingly
        buffer(
          this.fetchDataTrigger$$.pipe(
            debounce(() =>
              this.blazeTableStore.actionsInProgress$.pipe(map((number) => number === 0)),
            ),
          ),
        ),
        switchMap((bufferedValues) => {
          if (bufferedValues.includes(true) && bufferedValues.includes(false)) {
            return this.fetchData(false).pipe(switchMap(() => this.fetchData(true)));
          } else if (bufferedValues.includes(true)) {
            return this.fetchData(true);
          }
          return this.fetchData(false);
        }),
        untilDestroyed(this),
      )
      .subscribe({
        next: (data) => {
          this.dataUpdated$$.next(data);
          this.viewRefresh$$.next(true);
        },
      });

    this.shouldAllowConvertIntoAccessRole$
      .pipe(
        distinctUntilChanged(),
        switchMap((shouldAllowConvertIntoAccessRole) =>
          tablesApiService.getAllowedConversions(shouldAllowConvertIntoAccessRole),
        ),
        untilDestroyed(this),
      )
      .subscribe((res) => (this.allowedConversions = res));

    this.showError = this.showError.bind(this);

    this.updateRow$
      .pipe(
        untilDestroyed(this),
        tap((rowInfo) => {
          if (rowInfo.rowNum !== undefined) {
            this.rowsToUpdate[rowInfo.rowNum] = rowInfo;
          }
        }),
        debounceTime(1000),
      )
      .subscribe(() => {
        while (this.rowsToUpdate.length) {
          const rowToUpdate = this.rowsToUpdate.pop();
          if (rowToUpdate) {
            this.updateRow(rowToUpdate);
          }
        }
      });

    fromEvent(window, 'resize')
      .pipe(untilDestroyed(this), debounceTime(100))
      .subscribe(() => this.cdr.detectChanges());

    // Actions performed upon closing Blaze Table editor
    this.config$
      .pipe(
        withLatestFrom(this.store.select(fromRouter.getProjectId)),
        takeUntil(destroyedNoActionsPending$), // Allow the pending actions are finished after closing the dialog
        switchMap(([, projectId]) => {
          // switchMap as the last, so it's able to finish the request if triggered
          return this.tablesCollService.getWithQuery({ projectId });
        }),
        finalize(() => {
          // After the dialog closes and actions get finalized
          // Inform the system about the updated Blaze Table config
          const schema = this.schemaService.processBlazeTable(this.config);
          this.store.dispatch(blazeActions.blazeTableUpdated({ table: this.config, schema }));
          if (this.refreshSchemaAfterClose && this.config?.id) {
            this.schemaService.datasetIdsToRefresh$.next([this.config.id]);
          }
        }),
      )
      .subscribe();

    this.config$.pipe(untilDestroyed(this)).subscribe((config) => {
      this.shouldAllowConvertIntoAccessRole$.next(
        config?.systemTableType === SystemTableType.Users &&
          !config.columns.some((col) => col.isAccessRole),
      );
      this.configEmitter.emit({ config });
    });

    this.initTaskPoolingLogic();
    this.initializeCellEdit();

    this.initializeActionsInProgressTracking();
  }

  ngOnInit(): void {
    this.table$.pipe(untilDestroyed(this)).subscribe((table) => {
      this.updateConfig(table.config);
      this.updateColumns(this.blazeTableService.prepareColumns(table.config));
      this.sort = { columnName: this.findColumnId('_id'), direction: 'Descending' };
      this.pageToken = table.pageToken || undefined;
      this.totalCount = table.totalCount || 0;
      this.rows = [...table.data];
      this.recheckViewportSize = true;
      this.viewRefresh$$.next(true);
    });

    this.blazeTableStore.sourceColumn$.pipe(untilDestroyed(this)).subscribe((column) => {
      this.sourceColumn = column;
      this.viewRefresh$$.next(true);
    });
    this.blazeTableStore.actionType$.pipe(untilDestroyed(this)).subscribe((actionType) => {
      this.columnActionType = actionType;
    });

    this.config$
      .pipe(
        map((config) => config?.id),
        filter(Boolean),
        distinctUntilChanged(),
        switchMap((tableId) =>
          this.tablesApiService
            .getTableReferences(tableId)
            .pipe(catchError(() => of({ references: {} as Record<ColumnId, ReferenceInfo[]> }))),
        ),
        untilDestroyed(this),
      )
      .subscribe(({ references }) => {
        this.columnReferences = references;
        safeDetectChanges(this.cdr);
      });

    this.tablesCollService.entities$.pipe(untilDestroyed(this), skip(1)).subscribe((tables) => {
      const tableConfig = tables?.find(({ id }) => !isNullish(id) && id === this.config?.id);
      if (tableConfig) {
        const config = this.blazeTableService.syncConfigWithSchema(tableConfig);
        this.updateColumns(this.blazeTableService.prepareColumns(config));

        this.viewRefresh$$.next(true);
      }
    });

    this.rowFocusIndex$$
      .pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe((currentRowIndex) => {
        const previous = this.rowFocusIndex;
        if (
          previous !== undefined &&
          previous > -1 &&
          this.rows[previous] &&
          !this.rows[previous]?.['_id']
        ) {
          this.saveTableRow(previous);
        } else if (
          previous === -1 &&
          this.newRow &&
          Object.values(this.newRow)?.filter(Boolean).length
        ) {
          this.saveTableRow(-1, true);
        }
        this.rowFocusIndex = currentRowIndex;
      });
  }

  ngAfterViewInit(): void {
    if (this.scrollable) {
      this.registerVirtualScroll(this.scrollable);
    }

    this.handleNextPageFetching();

    if (this.cells) {
      this.updateRowHeightOnCellsChange(this.cells);
    }

    if (this.tableRows) {
      this.refreshViewportOnRowHeightChange(this.tableRows);
    }
  }

  ngOnDestroy(): void {
    this.progressDialogConfig$$.next(undefined);
    this.componentDestroy$.next(true);
  }

  getColumnReferences(column: DatasetColumn): ReferenceInfo[] {
    if (!column.id) {
      return [];
    }

    return this.columnReferences[column.id] ?? [];
  }

  addTableColumn(elementRef: HTMLElement): void {
    this.blazeTableStore.setActionType(BlazeTableActionType.Add);
    this.blazeTableStore.setSourceColumn(this.columns[this.columns.length - 1]);
    this.blazeTableStore.setPendingChanges(false);

    this.openColumnEditorPanel(elementRef);
  }

  addTableRow(): void {
    const newRow: DatasetRow = {};
    const columns = this.blazeTableService.prepareColumns(this.config);
    columns.forEach((col) => {
      if (Array.isArray(col?.defaultValue)) {
        newRow[col.name] = col.defaultValue.filter(Boolean);
      } else if (hasNoneDefaultValue(col)) {
        newRow[col.name] = null;
      } else {
        newRow[col.name] = col.defaultValue ?? null;
      }
    });
    if (this.totalCount <= this.rows.length) {
      this.virtualScroll?.scrollToIndex(this.rows.length);
      this.rows = [...this.rows, newRow];
      this.patchNewRowWithComputedValues(newRow, false, this.rows.length - 1);
    } else {
      this.newRow = newRow;
      this.patchNewRowWithComputedValues(newRow, true, this.rows.length - 1);
    }
    this.viewRefresh$$.next(true);
    setTimeout(() => {
      const el = (this.elRef.nativeElement as HTMLElement).querySelector<HTMLElement>(
        FIRST_CELL_FROM_LAST_ROW_SELECTOR,
      );
      el?.focus();
      el?.click();
    }, 200);
  }

  saveTableRow(index: number, isFooter?: boolean): void {
    if (!this.rows.length) {
      return;
    }

    const row = isFooter ? this.newRow : this.rows[index];

    if (!row || row?.['_id']) {
      return;
    }

    iif(
      () => row?._id,
      of(row),
      this.datasetsApiService.postInsert({
        datasetId: this.config.id,
        includeRowId: false,
        row: this.prepareRowDataForSaving(row),
      }),
    )
      .pipe(this.countAction())
      .subscribe({
        next: (row) => {
          if (isFooter) {
            this.rows.push(row);
            this.newRow = undefined;
          } else {
            this.rows[index] = row;
          }
          this.totalCount++;
          this.notificationService.success('Row added');
          this.viewRefresh$$.next(true);
        },
        error: this.showError(row, index),
      });
  }

  closeAddColumnDialog(): void {
    this.blazeTableStore.setSourceColumn(null);
    this.blazeTableStore.setPendingChanges(false);
    this.viewRefresh$$.next(true);
  }

  deleteTableRow(event: Event, rowIndex?: number, isFooter?: boolean): void {
    if (rowIndex === undefined || rowIndex === -1) {
      return;
    }

    event.preventDefault();

    if (isFooter) {
      this.newRow = undefined;
      return;
    }

    const row = this.rows[rowIndex];

    if (!row) {
      return;
    }

    this.rows.splice(rowIndex, 1);
    this.rows = [...this.rows];
    this.totalCount = this.rows.length;

    this.viewRefresh$$.next(true);

    if (!row?._id) {
      delete this.errorFields[`newRow${rowIndex}`];
      return;
    }

    this.datasetsApiService
      .postDelete({
        cascadeDeleteOption: undefined,
        datasetId: this.config.id,
        ignoreResult: true,
        includeRowId: false,
        queryNode: undefined,
        rowId: row._id,
        workflowExecutionId: undefined,
        workflowId: undefined,
      })
      .pipe(this.countAction())
      .subscribe({
        error: this.showError(),
      });
  }

  @HostListener('document:click', ['$event'])
  @HostListener('document:keyup.tab', ['$event'])
  @HostListener('document:focusout', ['$event'])
  checkFocusEvent(event: FocusEvent | KeyboardEvent | Event): void {
    const target = event.target as HTMLElement;
    // query list order can be messed after list update, need to use native selectors
    const rows = Array.from(document.getElementsByClassName('table-row'));
    rows.pop();
    rows.shift();
    const row = rows.find((row) => row.contains(target));
    const index = row ? this.getRowIndex(row) : -1;
    // TOOD FIX: we accept event to be FocusEvent | KeyboardEvent but later we cast it to FocusEvent -> to fix/split code branches
    const relatedTarget = (event as FocusEvent).relatedTarget as HTMLElement;
    const removeButton =
      target.closest?.('td')?.getElementsByClassName('delete-row-button')[0] ||
      (relatedTarget?.classList?.contains('delete-row-button') && relatedTarget);
    // when it's remove button being clicked we ignore this action and set focus index to -1
    if (
      index > -1 &&
      removeButton &&
      rows[index]?.contains(removeButton) &&
      ['click', 'focusout'].includes(event.type)
    ) {
      this.rowFocusIndex = -1;
      return;
    }

    // when focus is in other overlay or dock panel, we ignore this action
    const panelRoot =
      this.table?.nativeElement.closest('blaze-panel-outlet, .cdk-overlay-pane') ??
      this.table?.nativeElement;
    if (!panelRoot?.contains(target)) {
      return;
    }
    this.rowFocusIndex$$.next(index);
  }

  @HostListener('window:keydown.delete', ['$event'])
  deleteMultipleRows(event: KeyboardEvent | Event): void {
    const selectedRows = Object.keys(this.selectedRows);
    if (event instanceof KeyboardEvent && event.key === 'Delete' && selectedRows.length) {
      const title =
        selectedRows.length === 1 ? 'Removing 1 row' : `Removing ${selectedRows.length} rows`;
      this.dialogService
        .openConfirmDialog({
          confirmLabel: 'Yes, delete',
          description: 'Are you sure you want to remove all selected rows?',
          title,
          cancelLabel: 'No, keep it',
        })
        .afterClosed()
        .pipe(
          untilDestroyed(this),
          filter((value) => !!value),
          switchMap(() => {
            if (selectedRows.every((v) => typeof v === 'number')) {
              return of(true);
            }

            const query: LogicalPredicateNode = {
              nodeType: PredicateType.Logical,
              logicalType: LogicalType.And,
              children: [
                {
                  names: ['_id'],
                  op: ComparisonOperator.In,
                  value: selectedRows.filter((v) => typeof v === 'string'),
                  nodeType: PredicateType.Comparison,
                },
              ],
            };

            return this.datasetsApiService.postDelete({
              datasetId: this.config.id,
              ignoreResult: true,
              queryNode: query,
            });
          }),
          this.countAction(),
        )
        .subscribe({
          next: () => {
            selectedRows.forEach((row) => {
              if (typeof row === 'string') {
                const rowIndex = this.rows.findIndex((v) => v?._id === row);
                if (rowIndex > -1) {
                  this.rows.splice(rowIndex, 1);
                }
              } else {
                this.rows.splice(row, 1);
              }
            });
            this.totalCount = this.totalCount - selectedRows.length;
            this.selectedRows = {};
            this.viewRefresh$$.next(true);
          },
          error: this.showError(),
        });
    }
  }

  /**
   * Checks whether a column name can be used. If not, it normalizes its new name. Once the name is changed,
   * it updates rows and their data to use new column name.
   */
  editColumnName(column: DatasetColumn, $event: FocusEvent): void {
    if (!this.sourceColumn) {
      return;
    }

    let name = ($event.target as HTMLInputElement).value;
    // We need to make a copy of the source column because when the subscribe method below is called, we're not
    // guaranteed that the sourceColumn hasn't changed in the meantime.
    const sourceColumn = deepClone(this.sourceColumn);

    if (name === sourceColumn.name) {
      return;
    }

    const result = this.blazeTableService.validateColumnName(
      name,
      sourceColumn,
      this.config.columns,
    );

    if (!result.isValid && result.error.code !== 'NOT_UNIQUE') {
      this.notificationService.error(result.error.message);
      this.viewRefresh$$.next(true);
      return;
    } else if (!result.isValid) {
      name = this.blazeTableService.normalizeColumnName(name, this.config.columns);
    }

    const prevColName = sourceColumn.name;

    this.tablesApiService
      .postRenameColumn(this.config.id, { columnName: prevColName, newColumnName: name })
      .pipe(
        map((dataset) => convertBlazeTableSchemaToBlazeTable(dataset)),
        this.countAction(),
      )
      .subscribe({
        next: (table) => {
          column.name = name;

          this.updateColumns(this.blazeTableService.prepareColumns(table));

          if (table?.columns?.length === 1 || table.displayColumn === prevColName) {
            table.displayColumn = name;
          }

          if (prevColName === this.sort.columnName) {
            this.sort.columnName = this.findColumnId(name);
          }

          if (sourceColumn) {
            this.rows = this.blazeTableService.updateTableRowsAfterEdit(
              this.rows,
              sourceColumn,
              column,
            );
          }

          this.updateConfig(table);
          this.viewRefresh$$.next(true);
        },
        error: this.showError(),
      });
  }

  getCellValue(rowIndex: number, colName: string, isFooter?: boolean): number | boolean | string {
    if (isFooter) {
      return this.newRow?.[colName];
    } else if (rowIndex < this.rows.length) {
      return this.rows[rowIndex]?.[colName];
    } else {
      return this.rows[rowIndex - this.rows.length]?.[colName];
    }
  }

  getRowError(
    row: DatasetRow | undefined,
    col: DatasetColumn,
    index?: number,
    isFooter?: boolean,
  ): string {
    let rowId = row?._id;
    if (isFooter) {
      rowId = 'newRow-1';
    } else if (!rowId) {
      rowId = 'newRow' + index;
    }
    const colId = col['name'];
    return rowId && colId && this.errorFields[rowId] ? this.errorFields[rowId][colId] : '';
  }

  isHiddenColumn(column: DatasetColumn): boolean {
    return column.isInternal ?? false;
  }

  isAllSelected(): boolean {
    return !!this.rows.length && Object.keys(this.selectedRows).length === this.rows.length;
  }

  onRowSelection(index: number, id?: string): void {
    if (this.selectedRows[id ?? index]) {
      delete this.selectedRows[id ?? index];
    } else {
      this.selectedRows[id ?? index] = true;
    }
  }

  onAllRowsSelection(event: BlazeCheckboxChange): void {
    if (event.checked) {
      this.rows.forEach((row, index) => {
        this.selectedRows[row?._id ?? index] = true;
      });
    } else {
      this.selectedRows = {};
    }
  }

  isProtectedColumn(column: DatasetColumn): boolean {
    return column.isProtected ?? false;
  }

  isDisplayColumnProtected(): boolean {
    if (!this.config.displayColumn) {
      return false;
    }

    return this.schemaService.isProtectedColumn(this.config.displayColumn, this.config.id);
  }

  onMenuClosed(): void {
    this.columnEditorVisible$.next(false);
  }

  onMenuOpened(): void {
    this.columnEditorVisible$.next(true);
  }

  /**
   * Called when configuration of the column has completed and data needs to be processed.
   * @param column
   */
  processConfiguredColumn(column: DatasetColumn): void {
    if (!column || !this.sourceColumn) {
      return;
    }
    this.execColumnAction(
      {
        sourceColumn: this.sourceColumn,
        type: this.columnActionType,
        payload: column,
      },
      false,
    ).subscribe(() => {
      this.schemaService.datasetSchemaUpdated(this.config.id);
      this.viewRefresh$$.next(true);
    });
  }

  setMouseOverRow(index?: number): void {
    this.mouseOverRowIndex = index;
  }

  /**
   * Initializes appropriate column action based on the option selected from table header option picker.
   */
  startColumnAction(action: BlazeTableAction, index: number): void {
    this.blazeTableStore.setActionType(action.type);
    this.blazeTableStore.setSourceColumn(action.sourceColumn);
    this.blazeTableStore.setPendingChanges(false);
    switch (action.type) {
      case BlazeTableActionType.Delete:
      case BlazeTableActionType.Rename:
      case BlazeTableActionType.SetAsDisplayColumn:
      case BlazeTableActionType.SortAZ:
      case BlazeTableActionType.SortZA:
        this.execColumnAction(action).subscribe();
        break;
      case BlazeTableActionType.Edit:
      case BlazeTableActionType.InsertLeft:
      case BlazeTableActionType.InsertRight: {
        const htmlTarget = this.addColumnTrigger.get(index);
        if (!htmlTarget) {
          break;
        }
        this.openColumnEditorPanel(htmlTarget.nativeElement);
        break;
      }
      case BlazeTableActionType.FindUsages:
        this.openColumnUseDialog(this.getColumnReferences(action.sourceColumn));
        break;
      default:
        break;
    }
  }

  updateFieldValue(
    rowNum: number,
    row: DatasetRow | null,
    column: DatasetColumn,
    value: BlazeTableValue,
    isFooterRow?: boolean,
  ): void {
    const rowToUpdate = isFooterRow ? { ...this.newRow } : { ...this.rows[rowNum] };
    rowToUpdate[column.name] = value;

    if (isFooterRow) {
      this.newRow = rowToUpdate;
    } else if (rowNum > NEW_ROW_INDEX) {
      this.rows[rowNum] = rowToUpdate;
    } else {
      this.rows[rowNum - this.rows.length] = rowToUpdate;
      this.viewRefresh$$.next(true);
    }

    const rowId = row?._id || 'newRow' + rowNum;
    if (this.errorFields?.[rowId]?.[column.name]) {
      delete this.errorFields[rowId][column.name];

      if (Object.keys(this.errorFields[rowId]).length === 0) {
        delete this.errorFields[rowId];
      }
    }

    if (!rowToUpdate?._id) {
      this.patchNewRowWithComputedValues(rowToUpdate, isFooterRow, rowNum);
      return;
    }

    if (this.isEmptyRow(this.rows[rowNum])) {
      this.notificationService.error("You can't save empty row");
      return;
    }

    if (!isFooterRow) {
      this.updateRow$.next({ row, rowNum });
    }
    safeDetectChanges(this.cdr);
  }

  patchNewRowWithComputedValues(
    row: DatasetRow | null | undefined,
    isFooterRow: boolean | undefined,
    rowNum: number,
  ): void {
    if (!row) {
      return;
    }
    const columnsComputed = this.columns
      .filter((col) => (col.formula || col.isComputed || col.isInline) && !col.isInternal)
      .map((col) => col.name);
    this.valuePatchStop$.next(true);
    if (columnsComputed.length) {
      this.datasetsApiService
        .postInsert({
          datasetId: this.config.id,
          dryRun: true,
          includeRowId: false,
          row: this.prepareRowDataForSaving(row),
        })
        .pipe(this.countAction(), takeUntil(this.valuePatchStop$), untilDestroyed(this))
        .subscribe((res) => {
          const computedValues = Object.fromEntries(
            Object.entries(res).filter(([key]) => columnsComputed.includes(key)),
          );
          if (isFooterRow) {
            this.newRow = { ...this.newRow, ...computedValues };
            safeDetectChanges(this.cdr);
          } else if (!this.rows[rowNum]?.['_id']) {
            this.rows[rowNum] = { ...this.rows[rowNum], ...computedValues };
            safeDetectChanges(this.cdr);
          }
        });
    }
  }

  updateRow({
    row,
    rowNum,
  }: {
    row: DatasetRow | null;
    rowNum: number | undefined;
  }): Subscription | Observable<undefined> {
    if (rowNum === undefined || !this.rows[rowNum] || !this.rows[rowNum]?._id) {
      return of(undefined);
    }
    const rowToProceed = this.rows[rowNum] as DatasetRow;
    return this.datasetsApiService
      .postUpdate(
        this.config.id,
        {
          value: this.prepareRowDataForSaving(rowToProceed),
          upsert: false,
          queryNode: {
            nodeType: PredicateType.Logical,
            logicalType: LogicalType.And,
            children: [
              {
                names: ['_id'],
                op: ComparisonOperator.Eq,
                value: rowToProceed._id,
                nodeType: PredicateType.Comparison,
              },
            ],
          },
        },
        false,
      )
      .pipe(this.countAction())
      .subscribe({
        next: (updatedRow) => {
          this.rows[rowNum] = updatedRow;
          delete this.errorFields[updatedRow._id];
          this.viewRefresh$$.next(true);
        },
        error: this.showError(row ?? undefined, rowNum),
      });
  }

  moveColumn(test: CdkDragDrop<any>): void {
    const table = deepClone(this.config);
    const prevIndex = findColumnIndex(table.columns, this.columns[test.previousIndex]);
    const currIndex = findColumnIndex(table.columns, this.columns[test.currentIndex]);

    table.columns = moveItemInArrayImmutably(table.columns, prevIndex, currIndex);
    this.updateColumns(this.blazeTableService.prepareColumns(table));

    this.updateConfig(table);

    this.updateTableConfig().pipe(this.countAction()).subscribe({
      error: this.showError(),
    });
  }

  getColumnTypeIcon(column: DatasetColumn): string {
    if (column.name === this.mouseOverCol?.name) {
      return 'icon_drag_8';
    }

    return this.blazeTableService.getColumnTypeIcon(column, column.isInline);
  }

  setMouseOverCol(col: DatasetColumn | null): void {
    if (!this.isDraggable) {
      // Change icon only if the column is draggable.
      this.mouseOverCol = null;
    } else {
      this.mouseOverCol = col;
    }
    this.viewRefresh$$.next(true);
  }

  filter(filters: LogicalQueryNode | undefined): void {
    this.filters.set(filters);
    this.fetchDataTrigger$$.next(false);
  }

  copy(event: MouseEvent, columnTemplate: string, value: string): void {
    let valueToCopy: string;
    if (columnTemplate === 'booleanTpl') {
      valueToCopy = value ? 'true' : 'false';
    } else if (['imageTpl', 'fileTpl'].includes(columnTemplate)) {
      valueToCopy = value?.toString() ?? '';
    } else {
      const parentEl = (event.currentTarget as HTMLElement).parentElement;
      const rowNumberCellContent = parentEl?.getElementsByClassName('row-num')?.[0]?.textContent;
      valueToCopy = parentEl?.textContent?.replace(rowNumberCellContent ?? '', '')?.trim() || '';
    }

    navigator.clipboard.writeText(valueToCopy).then(() => {
      this.notificationService.success(`Cell value copied`, true);
    });

    event.stopPropagation();
  }

  renameTable(name: string): void {
    const config = deepClone(this.config);
    config.name = name;
    this.updateConfig(config);

    this.updateTableConfig().subscribe({
      error: (err) => {
        this.refetchTable();
        this.notificationService.showError(err);
        safeDetectChanges(this.cdr);
      },
    });
  }

  /**
   * Copies data from the specified source environment
   */
  copyDataFromEnvironment(sourceEnvironment: EnvironmentName): void {
    this.progressDialogConfig$$.next({ description: 'Copying Data', allowCancel: true });
    this.preventChangingProgressDialogData = true;

    this.progressDialogClosed$.pipe(first()).subscribe((isClosed) => {
      if (isClosed) {
        this.fetchData().pipe(first()).subscribe();
      }
    });
    this.tablesApiService
      .copyTableDataToEnvironment(this.config.id, sourceEnvironment)
      .pipe(
        catchError((err) => {
          this.notificationService.showError(err);
          this.progressDialog?.close();
          throw err;
        }),
        finalize(() => {
          this.preventChangingProgressDialogData = false;
        }),
      )
      .subscribe((job) => {
        this.progressDialogConfig$$.next({ taskId: job.id });
      });
  }

  exportToCSV(): void {
    this.progressDialogConfig$$.next({ description: 'Exporting file', allowCancel: true });

    this.preventChangingProgressDialogData = true;
    this.tablesApiService
      .getTableCSV(this.config.id)
      .pipe(
        catchError((err) => {
          this.notificationService.showError(err);
          this.progressDialog?.close();
          throw err;
        }),
        finalize(() => {
          this.preventChangingProgressDialogData = false;
        }),
      )
      .subscribe((job) => {
        this.progressDialogConfig$$.next({ taskId: job.id });
      });
  }

  importCSV(file?: File): void {
    if (!file) {
      return;
    }

    this.progressDialogConfig$$.next({ description: 'Uploading file', allowCancel: true });

    this.preventChangingProgressDialogData = true;

    this.tablesApiService
      .postUpdateTableFromCsvWithJobDetails(this.config.id, file)
      .pipe(
        takeUntil(this.progressDialogClosed$),
        tap((input: any) => {
          if (input.type === HttpEventType.UploadProgress) {
            this.progressDialogConfig$$.next({
              percent:
                input.total !== undefined ? Math.round((input.loaded * 100) / input.total) : 0,
            });
          }
        }),
        catchError((err) => {
          this.notificationService.showError(err);
          this.progressDialog?.close();
          throw err;
        }),
        filter(
          (event: HttpEvent<JobInfo<BlazeTableSchema>> & { body: JobInfo<BlazeTableSchema> }) =>
            event.type === HttpEventType.Response,
        ),
      )
      .subscribe({
        next: ({ body }) => {
          this.progressDialogConfig$$.next({ percent: null, taskId: body.id });
          this.taskFetchPolingTrigger$.next(true);
          this.refreshDataset.emit();
          this.handleNextPageFetching();
        },
        complete: () => {
          this.preventChangingProgressDialogData = false;
        },
      });
  }

  isEmptyRow(row?: DatasetRow): boolean {
    if (!row) {
      return true;
    }
    const data = this.prepareRowDataForSaving(row);
    return !this.columns.some((column) => data[column.name] || data[column.name] === 0);
  }

  calculatePreviewHeight(): void {
    const parentHeight = this.table?.nativeElement.closest('blaze-table')?.clientHeight || 0;
    this.dragPreviewHeight = parentHeight - 39;
  }

  setIsDragging(isDragging: boolean): void {
    this.isDragging = isDragging;
  }

  setDraggedColumn(column: DatasetColumn | null): void {
    this.draggedColumn = column;

    if (this.draggedColumn === null) {
      this.draggedColumnsPreview = null;
    }
  }

  setDraggedColumnsPreview($event: CdkDragSortEvent): void {
    this.draggedColumnsPreview = moveItemInArrayImmutably(
      this.draggedColumnsPreview || this.columns,
      $event.previousIndex,
      $event.currentIndex,
    );
  }

  openColumnEditorPanel(columnTarget: HTMLElement): void {
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(columnTarget)
      .withPositions([{ originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' }])
      .withGrowAfterOpen(true)
      .withFlexibleDimensions(true)
      .withPush(true);
    const overlayRef = this.overlay.create({
      positionStrategy,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
    });
    overlayRef
      .backdropClick()
      .pipe(withLatestFrom(this.blazeTableStore.pendingChangesForColumnEdit$))
      .subscribe(([_, pendingChanges]) => {
        if (pendingChanges) {
          this.dialogService
            .openConfirmDialog({
              confirmLabel: 'Yes, close',
              title: 'Unsaved changes',
              description:
                'You have unsaved changes. Closing the column editor will discard them. Do you want to continue?',
              cancelLabel: 'Cancel',
            })
            .afterClosed()
            .subscribe((confirm) => {
              if (confirm) {
                this.closeAddColumnDialog();
                overlayRef.detach();
                overlayRef.dispose();
              }
            });
        } else {
          this.closeAddColumnDialog();
          overlayRef.detach();
          overlayRef.dispose();
        }
      });

    const editorComponent = new ComponentPortal(ColumnEditorPanelComponent);

    const componentRef = overlayRef.attach(editorComponent);

    // reposition when AI CHAT opened
    afterNextRender(
      () => {
        const panelRect = componentRef.instance.host.nativeElement.getBoundingClientRect();
        const hostRect = this.elRef.nativeElement.getBoundingClientRect();
        if (panelRect.right > hostRect.right) {
          positionStrategy.withPositions([
            { originX: 'start', originY: 'bottom', overlayX: 'center', overlayY: 'top' },
          ]);
          overlayRef.updatePosition();
        }
      },
      { injector: this.injector },
    );

    componentRef.setInput('tableConfig$', this.config$);
    componentRef.setInput('tableId', this.config.id);
    componentRef.setInput('rowsCount', this.rows.length);
    componentRef.setInput('allowedConversions', this.allowedConversions);
    componentRef.instance.addField
      .pipe(untilDestroyed(componentRef.instance))
      .subscribe(($event) => {
        this.processConfiguredColumn($event);
        overlayRef?.detach();
        overlayRef?.dispose();
      });
    componentRef.instance.closePanel.pipe(untilDestroyed(componentRef.instance)).subscribe(() => {
      this.closeAddColumnDialog();
      overlayRef?.detach();
      overlayRef?.dispose();
    });

    componentRef.onDestroy(() => {
      this.blazeTableStore.deleteSourceColumn();
      overlayRef?.detach();
      overlayRef?.dispose();
    });
  }

  fileDropped(
    event: FileList,
    column: DatasetColumn,
    rowIndex: number,
    cell: HTMLTableCellElement,
    row: DatasetRow,
    value: any,
    error: any,
    isFooter: boolean,
    isFirst: boolean,
  ): void {
    this.cellClick(undefined, column, rowIndex, cell, row, value, error, isFooter, isFirst, {
      type: 'file-upload',
      file: event,
    });
  }

  cellClick(
    event: Event | undefined,
    column: DatasetColumn,
    rowIndex: number,
    cell: HTMLTableCellElement,
    row: DatasetRow,
    value: any,
    error: any,
    isFooter: boolean,
    isFirst: boolean,
    extraAction?: ExtraStartAction,
  ): void {
    event?.preventDefault();
    event?.stopImmediatePropagation();
    if (event) {
      this.checkFocusEvent(event as FocusEvent);
    }
    if (this.isColumnEditable(column) || this.allowNewRowException(column, rowIndex)) {
      this.cellEdit$$.next({
        column,
        rowIndex,
        cell,
        row,
        value,
        error,
        isFooter,
        isFirst,
        extraAction,
      });
    }
  }

  @HostListener('document:keypress', ['$event'])
  @HostListener('document:keydown', ['$event'])
  async preventActions(event: KeyboardEvent): Promise<void> {
    if (event.key !== 'Tab') {
      return;
    }

    // when opened datetime field input && target is date picker then allow to swap to time with default tab action
    const target = event.target as HTMLElement;
    if (!event.shiftKey && target.tagName === 'INPUT' && target.classList.contains('datetime')) {
      return;
    }

    const { prev, next } = this.siblingColsForActiveCell;
    if (event.shiftKey && prev) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const elRef = this.elRef.nativeElement;
      await this.scrollCellIntoViewWhenNeeded(prev);
      if (prev instanceof HTMLElement) {
        prev.focus();
        prev.click();
      } else if ('rowIndex' in prev) {
        const relatedElement = elRef.querySelector(
          `td[data-column="${prev.column}"][aria-rowindex="${prev.rowIndex}"]`,
        );
        relatedElement?.focus();
        relatedElement?.click();
      }
    } else if (!event.shiftKey && next) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const elRef = this.elRef.nativeElement;
      await this.scrollCellIntoViewWhenNeeded(next);
      if (next instanceof HTMLElement) {
        next.focus();
        next.click();
      } else if ('rowIndex' in next) {
        const relatedElement = elRef.querySelector(
          `td[data-column="${next.column}"][aria-rowindex="${next.rowIndex}"]`,
        );
        relatedElement?.focus();
        relatedElement?.click();
      }
    } else if (!event.shiftKey && this.cellEdit$$.value) {
      const cell = this.cellEdit$$.value.cell;
      this.cellEdit$$.next(undefined);
      event.preventDefault();
      event.stopImmediatePropagation();
      cell?.focus();
      document.activeElement?.dispatchEvent(new KeyboardEvent('keypress', { key: 'Tab' }));
    }
  }

  findPrevAndNextColForTab(cellEditInfo?: CellEditInfo): void {
    let prev: (typeof this.siblingColsForActiveCell)['prev'],
      next: (typeof this.siblingColsForActiveCell)['next'];
    if (cellEditInfo) {
      if (!next) {
        const currentColumnName = cellEditInfo.cell.getAttribute('data-column');
        const currentTh: Element = this.elRef.nativeElement.querySelector(
          `th[data-columnName="${currentColumnName}"]`,
        );
        let nextTh: Element | null | undefined = currentTh.nextElementSibling;
        let rowIndex = cellEditInfo.rowIndex;

        // when it's last th we need to find from the beginning
        if (!nextTh?.nextElementSibling) {
          nextTh = currentTh.parentElement?.querySelector('th');
          rowIndex = rowIndex + 1;
        }
        // when it's last row we need to prevent going to next row
        if (rowIndex === this.rows.length) {
          rowIndex = -1;
        }
        while (nextTh?.nextElementSibling) {
          const colName = nextTh.getAttribute('data-columnName');
          if (colName && this.isColumnEditable(colName)) {
            // we found next editable column
            break;
          }
          // when it's last th we need to find from the beginning

          nextTh = nextTh.nextElementSibling;
          if (!nextTh?.nextElementSibling) {
            nextTh = currentTh.parentElement?.querySelector('th');
            rowIndex = rowIndex + 1;
          }
          if (nextTh?.getAttribute('data-columnName') === currentColumnName) {
            nextTh = undefined;
            break;
          }
        }
        const column = nextTh?.getAttribute('data-columnName');
        if (!column || rowIndex === -1) {
          this.logger.log('[⚡ Blaze] Table Edit: No column found for next cell');
        } else {
          next = {
            column,
            rowIndex,
          };
        }
      }
      if (!prev) {
        const currentColumnName = cellEditInfo.cell.getAttribute('data-column');
        const currentTh: Element = this.elRef.nativeElement.querySelector(
          `th[data-columnName="${currentColumnName}"]`,
        );
        let prevTh: Element | null | undefined = currentTh.previousElementSibling;
        while (prevTh?.previousElementSibling) {
          const colName = prevTh.getAttribute('data-columnName');
          if (colName && this.isColumnEditable(colName)) {
            prev = {
              column: colName,
              rowIndex: cellEditInfo.rowIndex,
            };
            break;
          }
          if (prevTh?.getAttribute('data-columnName') === currentColumnName) {
            prevTh = undefined;
            break;
          }
          prevTh = prevTh.previousElementSibling;
        }
        let rowIndex = cellEditInfo.rowIndex;
        if (!prevTh?.getAttribute('data-columnName')) {
          prevTh = Array.from(
            currentTh.parentElement?.querySelectorAll('th[data-columnName]') as NodeListOf<Element>,
          ).pop();
          rowIndex = rowIndex - 1;
        }
        const column = prevTh?.getAttribute('data-columnName');
        if (!column) {
          this.logger.log('[⚡ Blaze] Table Edit: No column found for prev cell');
        } else if (rowIndex > -1) {
          prev = {
            column,
            rowIndex,
          };
        }
      }
    }

    this.siblingColsForActiveCell = { prev, next };
    return;
  }

  private updateVirtualScrollViewportSize(): void {
    this.ngZone.runOutsideAngular(() => {
      runAfterFramePaint(() => {
        this.virtualScroll?.checkViewportSize();
      });
    });
  }

  private prepareRowDataForSaving(row: DatasetRow): DatasetRow {
    const excludedColumnTypes: ColumnType[] = ['CreatedTime', 'Formula', 'LastUpdatedTime'];
    const rowCopy = deepClone(row);

    this.columns.forEach((column) => {
      if (column.columnType && excludedColumnTypes.includes(column.columnType)) {
        delete rowCopy[column.name];
      }
    });

    return rowCopy;
  }

  private createFetchDataObservable(
    tableFetchConfig: {
      nextPage?: boolean;
      recheckViewportSize?: boolean;
    } = { nextPage: false, recheckViewportSize: false },
  ): Observable<PaginatedGetResponse | undefined> {
    return defer(() => {
      this.fetchDataTrigger$$.next(tableFetchConfig.nextPage ?? false);
      return this.dataUpdated$$.pipe(
        first(),
        tap(() => {
          if (tableFetchConfig.recheckViewportSize) {
            this.recheckViewportSize = tableFetchConfig.recheckViewportSize;
          }
        }),
      );
    });
  }

  private openColumnUseDialog(references: ReferenceInfo[]): void {
    this.dialogService.decorateDialogOpen(
      this.dialog.open(ColumnUseDialogComponent, {
        data: { references },
        disableClose: false,
        hasBackdrop: true,
        panelClass: ['bd', 'bd-dialog'],
        height: 'auto',
        width: '400px',
        maxWidth: '400px',
        maxHeight: '640px',
      }),
    );
  }

  /**
   * Execute column action based on the passed action. Make appropriate config or data updates.
   */
  private execColumnAction(action: BlazeTableAction, callDetectChanges = true): Observable<any> {
    let action$: Observable<any>;
    switch (action.type) {
      case BlazeTableActionType.Add:
        action$ = this.insertTableColumnRight(action);
        break;
      case BlazeTableActionType.Delete:
        action$ = this.deleteTableColumnAndUpdate(action);
        break;
      case BlazeTableActionType.Edit:
        action$ = this.editColumn(action);
        break;
      case BlazeTableActionType.InsertLeft:
        action$ = this.insertTableColumnLeft(action);
        break;
      case BlazeTableActionType.InsertRight:
        action$ = this.insertTableColumnRight(action);
        break;
      case BlazeTableActionType.Rename:
        action$ = this.selectRenameColumnField(action);
        break;
      case BlazeTableActionType.SetAsDisplayColumn:
        action$ = this.setAsDisplayColumnAndUpdate(action);
        break;
      case BlazeTableActionType.SortAZ:
      case BlazeTableActionType.SortZA:
        action$ = this.sortTableRows(action);
        break;
      default:
        action$ = of(undefined);
        break;
    }

    this.closeAddColumnDialog();

    return action$.pipe(
      tap(() => {
        if (callDetectChanges) {
          this.viewRefresh$$.next(true);
        }
      }),
      this.countAction(),
    );
  }

  private updateRowsAndColumnsAfterSuccessDelete(
    config: BlazeTable,
    column: DatasetColumn | DatasetColumn[] | undefined,
  ): void {
    this.blazeTableStore.requestRecordsUpdate({ column: undefined, config: undefined });
    if (!column) {
      return;
    }
    this.updateColumns(this.blazeTableService.prepareColumns(config));
    const columns = Array.isArray(column) ? column : [column];
    columns.forEach((col) => {
      this.rows = this.blazeTableService.updateTableRowsAfterDelete(this.rows, col);
    });
  }

  /**
   * Deletes specified column from table configuration. Updates rows and their data accordingly.
   */
  private deleteTableColumnAndUpdate(action: BlazeTableAction): Observable<BlazeTable | undefined> {
    const config = deepClone(this.config);
    return this.blazeTableService.deleteTableColumnUI(action.sourceColumn, config);
  }

  private editColumn(action: BlazeTableAction): Observable<unknown> {
    const table = deepClone(this.config);
    const colIndex = findColumnIndex(table.columns, action.sourceColumn);

    if (!action.payload || colIndex < 0) {
      return of(undefined);
    }

    const oldColumn = { ...this.config.columns[colIndex] };
    const newColumn = { ...action.payload };
    if (!isDeepEqual(oldColumn.tagDefinition, newColumn.tagDefinition)) {
      this.refreshSchemaAfterClose = true;
    }
    const name = newColumn.name;
    const requiredChange = newColumn.isRequired !== oldColumn.isRequired;
    const uniqueChange = newColumn.isUnique !== oldColumn.isUnique;

    table.columns[colIndex] = newColumn;
    if (table.columns?.length === 1 || this.config.displayColumn === oldColumn.name) {
      table.displayColumn = name;
    }

    if (oldColumn.name === this.sort.columnName) {
      this.sort.columnName = this.findColumnId(name);
    }

    return of(table).pipe(
      concatMap((table) => {
        // validate changes
        if (!oldColumn.link && newColumn.link && !newColumn.formula) {
          const sourceColumnName = action.sourceColumn.name;
          const targetTableId = newColumn.link.tableId;
          const targetColumnName = newColumn.link.columnName;
          return this.validateLink(table, sourceColumnName, targetTableId, targetColumnName);
        }

        return of(table);
      }),
      concatMap((table) =>
        this.blazeTableService.renameColumnWhenNeeded(table, oldColumn.name, newColumn.name),
      ),
      concatMap((table) => {
        // Column type change or updating the isSecure flag for 'File' column type
        // Conversion to 'Link' column type is not possible through this process; use updateTableConfig instead
        // Do not trigger the convert endpoint when changing a SingleLine column to a String column type
        // Do not trigger the convert endpoint when the column type is 'Formula' and only the formula is being updated
        const isChangeFromStringToSingleLine =
          newColumn.format?.formatType === 'SingleLine' &&
          !oldColumn.format?.formatType &&
          oldColumn.columnType === 'String';
        const hasLinkConversion =
          newColumn.link &&
          newColumn.columnType !== oldColumn.columnType &&
          !isLinkedInlineColumn(newColumn);

        const columnTypeChanged = newColumn.columnType !== oldColumn.columnType;
        // When column type is Rollup, format change doesn't require conversion, because it should be imported from source column
        const columnFormatChanged =
          newColumn.format?.formatType !== oldColumn.format?.formatType &&
          newColumn.columnType !== 'Rollup';
        const securityLevelChanged =
          newColumn.columnType === 'File' &&
          oldColumn.columnType === 'File' &&
          newColumn.isSecure !== oldColumn.isSecure;

        const isBothFormula =
          newColumn.columnType === 'Formula' && oldColumn.columnType === 'Formula';

        if (
          !isChangeFromStringToSingleLine &&
          (columnTypeChanged || columnFormatChanged || securityLevelChanged || hasLinkConversion) &&
          !isBothFormula
        ) {
          return this.tablesApiService.postConvertColumn(table.id, newColumn).pipe(
            map((affected) => {
              table.columns = [...affected.columns];
              const config = deepClone(this.config);
              config.columns = [...affected.columns];
              this.updateConfig(config);
              return table;
            }),
          );
        }
        return of(table);
      }),
      concatMap((table) => {
        // col linked columns changes
        if (
          newColumn.columnType === 'Link' ||
          (newColumn.columnType == 'Formula' && newColumn.link)
        ) {
          return this.editLinkedColumns(
            oldColumn as DatasetColumnExtendedForLinkEdit,
            newColumn as DatasetColumnExtendedForLinkEdit,
            table,
          );
        }
        return of(table);
      }),
      tap({ error: this.showError() }),
      concatMap((table) =>
        this.updateTableConfigAndFillEmptyColumns(
          table,
          newColumn.name,
          (requiredChange && newColumn.isRequired) || false,
        ),
      ),
      tap({ error: this.showError() }),
      tap(() => {
        if (!requiredChange && !uniqueChange) {
          return;
        }

        const { rowsToSave, rowsToUpdate } = extractRowsWithErrors(
          this.rows,
          this.errorFields,
          name,
        );

        // try to resend rows that have errors
        rowsToSave?.forEach((index) => this.saveTableRow(index));
        // try to resend update requests for rows that have errors
        rowsToUpdate?.forEach(({ row, rowNum }) => this.updateRow({ row, rowNum }));
      }),
      concatMap(() => this.createFetchDataObservable()),
    );
  }

  private updateEmptyColValues(
    table: BlazeTable,
    columnName: string,
    columnId: ColumnId,
    requiredChangedToTrue: boolean,
  ): Observable<{ table: BlazeTable; rowsUpdated: boolean }> {
    return this.blazeTableService.checkIfEmptyValuesInColumn(table, columnName).pipe(
      switchMap((emptyRows) => {
        if (emptyRows) {
          const description = requiredChangedToTrue
            ? `<br><br>If you choose to skip this step, the column will not be set as required.`
            : '';
          return this.dialogService
            .openConfirmDialog({
              title: 'Update Empty Rows',
              description:
                'Do you want to update empty rows in the column with the default value?' +
                description,
              confirmLabel: 'Yes, update',
              cancelLabel: 'No, skip',
            })
            .afterClosed()
            .pipe(
              untilDestroyed(this),
              switchMap((proceedUpdate) => {
                if (proceedUpdate) {
                  return this.tablesApiService
                    .patchEmptyColsWithDefaultValue(table.id, columnId)
                    .pipe(map(() => ({ table, rowsUpdated: true })));
                }
                return of({ table, rowsUpdated: false });
              }),
            );
        }
        return of({ table, rowsUpdated: true });
      }),
    );
  }

  private editLinkedColumns(
    oldColumn: DatasetColumnExtendedForLinkEdit,
    newColumn: DatasetColumnExtendedForLinkEdit,
    config: BlazeTable,
  ): Observable<BlazeTable> {
    let tableConfig = deepClone(config);
    const newColumns = newColumn.link?.inlineDatasetColumns;
    const columnId =
      (oldColumn.columnType === 'Formula' &&
        tableConfig.columns.find((c) => c.id && oldColumn.formula?.includes(c.id))?.id) ||
      oldColumn.id;
    const oldColumns = this.columns.filter(
      (c) => c.isInline && columnId && c.formula?.includes(columnId),
    );

    // if nothing changed - skip
    if (checkIfLinkedColumnsNotChanged(oldColumns, newColumns)) {
      this.fixTableConfigAfterLinkOperation(tableConfig);
      return of(tableConfig);
    }

    const columnsToRemove = getColumnsToRemove(oldColumns, newColumns);

    const columnsToAdd = getColumnsToAdd(oldColumns, newColumns);

    // remove columns that should be removed
    const colsToRemove: (DatasetColumn | undefined)[] = [];
    if (columnsToRemove) {
      columnsToRemove.forEach((column) => {
        colsToRemove.push(this.blazeTableService.deleteColumn(column, tableConfig));
      });
    }
    this.updateRowsAndColumnsAfterSuccessDelete(
      tableConfig,
      colsToRemove.filter((col): col is DatasetColumn => !!col),
    );

    // add new columns
    if (columnsToAdd?.length) {
      // create columns and filter only those interesting
      // change order to insert them later properly
      const columns = newColumns
        ?.map((nc) => {
          const oldColInstance = getLinkedColumnMatch(nc, oldColumns);
          if (oldColInstance) {
            return oldColInstance;
          } else if (columnId) {
            const newCol = {
              name: nc.name,
              columnType: 'Formula',
              dataType: undefined,
              formula: formatLinkedInlineColumnFormula(columnId, nc.id, nc.isLink),
            } as DatasetColumn;
            return this.blazeTableService.createColumn(newCol, this.columns);
          } else {
            return {} as DatasetColumn;
          }
        })
        .filter((c) => c.formula)
        .reverse();

      // insert new columns at right place
      let colIndex = findColumnIndex(tableConfig.columns, oldColumn);
      colIndex = colIndex >= 0 ? colIndex + 1 : tableConfig.columns.length;
      columns
        ?.filter((c) => c && !c.id)
        .forEach((column) => {
          if (!column?.id) {
            tableConfig = this.insertColumnAt(column, colIndex, tableConfig);
          }
        });
    }

    // if inline column edition fix the columnType
    if (newColumn.formula) {
      this.fixTableConfigAfterLinkOperation(tableConfig);
    }
    return this.updateTableConfig(tableConfig);
  }

  private fixTableConfigAfterLinkOperation(tableConfig: BlazeTable): BlazeTable {
    tableConfig.columns = produce(tableConfig.columns, (draft) => {
      draft.map((c) => {
        if (isLinkedInlineColumn(c)) {
          c.link = undefined;
          c.dataType = undefined;
        }
        return c;
      });
    });
    return tableConfig;
  }

  /**
   * Inserts column to the left of the source column.
   */
  private insertTableColumnLeft(
    action: BlazeTableAction,
  ): Observable<BlazeTable | PaginatedGetResponse | undefined> {
    if (action.payload) {
      const newCol = this.blazeTableService.createColumn(action.payload, this.columns);
      let colIndex = findColumnIndex(this.config.columns, action.sourceColumn);
      colIndex = colIndex >= 0 ? colIndex : 0;
      return this.insertColumnAtAndUpdate(newCol, colIndex).pipe(
        switchMap((response) => {
          return (action.payload?.link as DatasetLinkExtendedForLinkEdit | undefined)
            ?.inlineDatasetColumns?.length
            ? this.editLinkedColumns(this.config.columns[colIndex], newCol, this.config)
            : of(response);
        }),
      );
    }
    return of(undefined);
  }

  /**
   * Inserts column to the right of the source column.
   */
  private insertTableColumnRight(
    action: BlazeTableAction,
  ): Observable<BlazeTable | PaginatedGetResponse | undefined> {
    if (action.payload) {
      const newCol = this.blazeTableService.createColumn(action.payload, this.columns);
      let colIndex = findColumnIndex(this.config.columns, action.sourceColumn);
      colIndex = colIndex >= 0 ? colIndex + 1 : this.config.columns.length;
      return this.insertColumnAtAndUpdate(newCol, colIndex).pipe(
        switchMap((response) => {
          return (action.payload?.link as DatasetLinkExtendedForLinkEdit | undefined)
            ?.inlineDatasetColumns?.length
            ? this.editLinkedColumns(this.config.columns[colIndex], newCol, this.config)
            : of(response);
        }),
      );
    }
    return of(undefined);
  }

  private insertColumnAt(newColumn: DatasetColumn, index: number, config?: BlazeTable): BlazeTable {
    const tableConfig = config ?? this.config;
    tableConfig.columns = insertItemAt(tableConfig.columns, index, newColumn);
    this.updateColumns(this.blazeTableService.prepareColumns(tableConfig));
    this.rows = this.blazeTableService.updateTableRows(this.rows, newColumn);

    return tableConfig;
  }

  private insertColumnAtAndUpdate(
    column: DatasetColumn,
    index: number,
  ): Observable<PaginatedGetResponse | undefined> {
    this.insertColumnAt(column, index);
    const isDefaultValue = !!column.defaultValue;
    const requiredChangedToTrue: boolean = column.defaultValue && column.isRequired;
    return this.updateTableConfigAndFillEmptyColumns(
      this.config,
      column.name,
      requiredChangedToTrue,
    ).pipe(
      switchMap((response) => {
        if (!isDefaultValue) {
          return of(response);
        }

        const tableId = this.config.id;
        const columnId = this.config.columns?.find((col) => col.name === column.name)?.id;

        if (!tableId || !columnId) {
          return of(response);
        }

        return this.updateEmptyColValues(this.config, column.name, columnId, false).pipe(
          map(() => response),
        );
      }),
      mergeMap(() => this.createFetchDataObservable()),
      tap({ error: this.showError() }),
      catchError(() => {
        this.refetchTable();
        return of(undefined);
      }),
    );
  }

  /**
   * Marks cells as ones containing invalid data.
   * @param column - Column in which errors occur
   * @param linkedValueErrors - List of rows and reasons for which the error occured.
   */
  private markInvalidValues(
    columnName: string,
    linkedValueErrors: BlazeTableValidationError[],
  ): void {
    linkedValueErrors.forEach((valueError) => {
      const rowId = valueError?.rowId;
      if (!this.errorFields[rowId]) {
        this.errorFields[rowId] = {};
      }
      this.errorFields[rowId][columnName] = valueError.reason;
    });
    this.viewRefresh$$.next(true);
  }

  /**
   * Updates table configuration and fills empty columns with default values.
   * Order of operations:
   * 0. Prepare table config when requiredChangedToTrue is true
   * 1. Update table config
   * 2. Update empty column values
   * 3. Update table config again if requiredChangedToTrue is true
   * 4. Update table config in component
   */
  private updateTableConfigAndFillEmptyColumns(
    config: BlazeTable,
    columnName: string | undefined,
    requiredChangedToTrue: boolean,
  ): Observable<BlazeTable> {
    const newConfig = deepClone(config);

    // when no columnId then it is newly created column
    let editedColumnIndex = newConfig.columns.findIndex((col) => col.name === columnName);
    const isNullishDefaultValue = isNullish(newConfig.columns[editedColumnIndex].defaultValue);
    if (requiredChangedToTrue && !isNullishDefaultValue) {
      newConfig.columns[editedColumnIndex].isRequired = false;
    }

    return this.updateTableConfig(newConfig).pipe(
      switchMap((table) => {
        const newConfig = deepClone(table);
        editedColumnIndex = newConfig.columns.findIndex((col) => col.name === columnName);
        if (!isNullish(newConfig.columns[editedColumnIndex].defaultValue)) {
          const colName = newConfig.columns[editedColumnIndex].name;
          const colId = newConfig.columns[editedColumnIndex].id;
          if (!colId) {
            throw new Error('[Blaze Table] Column ID is undefined');
          }
          return this.updateEmptyColValues(table, colName, colId, requiredChangedToTrue);
        }
        return of({ table, rowsUpdated: false });
      }),
      switchMap(({ table, rowsUpdated }) => {
        const newConfig = deepClone(table);
        if (requiredChangedToTrue && rowsUpdated) {
          newConfig.columns[editedColumnIndex].isRequired = true;
          return this.updateTableConfig(newConfig);
        }
        return of(table);
      }),
      tap((table) => {
        const config = deepClone(this.config);

        config.columns = [...table.columns];
        if (table.displayColumn) {
          config.displayColumn = table.displayColumn;
        }
        this.updateConfig(config);
      }),
      map(() => this.config),
      this.countAction(),
    );
  }

  private updateTableConfig(config?: BlazeTable): Observable<BlazeTable> {
    const newConfig = deepClone(config ?? this.config);
    const columns = convertDatasetColumnsToColumnInfos(newConfig?.columns || []);
    columns.forEach((col) => {
      if (col.isInline) {
        delete col.format;
        delete col.precision;
      }
    });

    return this.tablesCollService.update({ ...newConfig, columns }).pipe(
      catchError((err) => {
        this.notificationService.showError(err);
        this.refetchTable();
        return throwError(() => err);
      }),
      map((dataset) => convertBlazeTableSchemaToBlazeTable(dataset)),
      tap((table) => {
        const config = deepClone(this.config);
        config.columns = [...table.columns];
        if (table.displayColumn) {
          config.displayColumn = table.displayColumn;
        }
        this.updateConfig(config);
      }),
      map(() => this.config),
      this.countAction(),
    );
  }

  /**
   * Configures given column as the display column.
   * @param action
   */
  private setAsDisplayColumnAndUpdate(action: BlazeTableAction): Observable<BlazeTable> {
    const config = deepClone(this.config);
    config.displayColumn = action.sourceColumn.name;
    this.updateConfig(config);

    return this.updateTableConfig().pipe(
      tap({
        error: this.showError(),
      }),
    );
  }

  /**
   * Selects contents of the appropriate column name input field.
   */
  private selectRenameColumnField(action: BlazeTableAction): Observable<undefined> {
    this.columnNames.forEach((elRef) => {
      if (elRef?.nativeElement?.getAttribute('data-column') === action.sourceColumn.name) {
        elRef?.nativeElement.select();
      }
    });
    return of(undefined);
  }

  private sortTableRows(action: BlazeTableAction): Observable<PaginatedGetResponse | undefined> {
    const columnName = getColumnKey(action.sourceColumn);
    const direction = action.type === BlazeTableActionType.SortAZ ? 'Ascending' : 'Descending';

    if (this.sort.columnName === columnName && this.sort.direction === direction) {
      return of(undefined);
    }

    this.sort = {
      columnName,
      direction,
    };

    return this.createFetchDataObservable();
  }

  /**
   * Validates linked data
   * @param sourceColumnName - Name of the column in source table that's going to be the link
   * @param targetTableId - To which table we're linking
   * @param targetColumnName - Name of the column in the target table to which we're linking
   */
  private validateLink(
    table: BlazeTable,
    sourceColumnName: string,
    targetTableId: string,
    targetColumnName: string,
  ): Observable<BlazeTable> {
    Object.entries(this.errorFields).forEach(([key, value]) => {
      delete value[sourceColumnName];

      if (Object.keys(value).length === 0) {
        delete this.errorFields[key];
      }
    });
    const isMultiSelect =
      table.columns.find((col) => col.name === sourceColumnName)?.link?.multipleValues || false;
    return this.tablesApiService
      .postValidateLink(table.id, sourceColumnName, targetTableId, targetColumnName, isMultiSelect)
      .pipe(
        switchMap((result) => {
          if (result.isValid) {
            return of(table);
          }

          this.markInvalidValues(sourceColumnName, result.mismatches);
          return EMPTY;
        }),
      );
  }

  private fetchData(more = false): Observable<PaginatedGetResponse> {
    const sort = { ...this.sort };
    const selectedSortColumn = findColumn(this.config.columns, {
      name: sort.columnName,
      id: sort.columnName,
    } as ColumnQueryLike);
    if (sort.columnName && selectedSortColumn) {
      sort.columnName = getColumnKey(selectedSortColumn);
    } else if (sort.columnName && this.config.displayColumn) {
      sort.columnName = this.findColumnId(this.config.displayColumn);
    }

    const filter = this.filters();

    return this.datasetsApiService
      .postGetPaginated(
        this.config.id,
        {
          pagination: {
            maxCount: 50,
            returnTotalCount: true,
            pageToken: more ? this.pageToken : undefined,
          },
          sort,
          filter: filter ?? null,
        },
        false,
      )
      .pipe(
        tap({
          next: (res) => {
            const notValidRows = this.rows.filter((r) => !r?._id);
            if (!more) {
              this.elRef.nativeElement.scrollTop = 0;
              this.rows = res.data;
              if (notValidRows.length) {
                this.rows.push(...notValidRows);
              }
            } else {
              this.rows = [...this.rows, ...res.data];
              if (notValidRows.length) {
                this.rows = [...this.rows, ...notValidRows];
              }
            }
            this.pageToken = res.pagination.nextPageToken;
            this.totalCount = res.pagination.totalCount || 0;
            this.viewRefresh$$.next(true);
          },
          error: (error) => {
            this.rows = [];
            this.pageToken = '';
            this.totalCount = 0;
            this.showError()(error);
            this.viewRefresh$$.next(true);
          },
        }),
        catchError(() => {
          return of({
            data: [],
            pagination: {
              nextPageToken: null,
              previousPageToken: null,
              totalCount: null,
            },
          });
        }),
      );
  }

  private showError(row?: DatasetRow | null, index?: number) {
    return (error: any): never => {
      if (row && (error.error.columnName || error.error.invalidColumns)) {
        const rowId = row._id || `newRow${index}`;
        delete this.errorFields[rowId];
        if (error.error.columnName) {
          this.markInvalidValues(error.error.columnName, [
            {
              rowId,
              reason: error.error.errorMessage,
            },
          ]);
        }
        if (error.error.invalidColumns) {
          error.error.invalidColumns.forEach(({ key, value }: { key: string; value: any }) => {
            this.markInvalidValues(key, [
              {
                rowId,
                reason: value,
              },
            ]);
          });
          this.markInvalidValues(error.error.columnName, [
            {
              rowId,
              reason: error.error.errorMessage,
            },
          ]);
        }
      } else {
        this.notificationService.showError(error);
      }

      throw error;
    };
  }

  /** Adjusts the action in progress counter, so it's known if there are pending actions. */
  private countAction<T>(): MonoTypeOperatorFunction<T> {
    return tap<T>({
      subscribe: () => this.blazeTableStore.incrementActionsInProgress(),
      finalize: () => this.blazeTableStore.decrementActionsInProgress(),
    });
  }

  private updateColumns(columns: DatasetColumn[]): void {
    this.columns = columns;
    this.deletableColumnsCount = this.columns.filter((c) => !c.isInline).length;
    this.updateColumnsMap(columns);
  }

  private updateColumnsMap(columns: DatasetColumn[]): void {
    const keys = Object.keys(COLUMN_TO_TEMPLATE_MAP);
    const columnsTemplates = Object.fromEntries(
      columns.map((col) => {
        const columnKeys = [col.format?.formatType, col.columnType, col.dataType];
        const keyToUse = columnKeys.find((key) => key && keys.includes(key));
        return [col.id, (keyToUse && COLUMN_TO_TEMPLATE_MAP[keyToUse]) || 'defaultTpl'];
      }),
    );
    this.columnsTemplates = columnsTemplates;
  }

  private findColumnId(columnName: string): string {
    return findColumnKey(this.config.columns, columnName) || columnName;
  }

  private initTaskPoolingLogic(): void {
    this.progressDialogConfig$$
      .pipe(untilDestroyed(this))
      .subscribe((config: ProgressDialogInput | undefined) => {
        if (!config && this.progressDialog) {
          this.progressDialogClosedSubscription?.unsubscribe?.();
          this.progressDialog.close();
          return;
        }

        if (!config) {
          return;
        }

        if (
          !this.progressDialog?.componentInstance ||
          this.progressDialog.getState() !== MatDialogState.OPEN
        ) {
          this.progressDialog = this.dialogService.openProgressDialog(config);
        } else if (this.progressDialog?.componentInstance) {
          this.progressDialog.componentInstance.patchConfig(config);
        }
        if (this.progressDialog) {
          this.progressDialogClosed$ = this.initProgressDialogOnClose();
          if (!this.preventChangingProgressDialogData) {
            this.progressDialogClosedSubscription?.unsubscribe?.();
            this.progressDialogClosedSubscription = this.progressDialogClosed$?.subscribe?.();
          }
        }
      });

    merge(this.taskFetchPolingTrigger$, timer(0, 5000))
      .pipe(
        withLatestFrom(this.config$, this.progressDialogConfig$$),
        filter(([, config]) => !!config?.id),
        exhaustMap(([, config, jobDialogConfig]) => {
          return (
            (jobDialogConfig?.taskId && this.jobsFacade.getJobsStatus(jobDialogConfig.taskId)) ||
            (config && this.jobsFacade.getDatasetJobs(config.id)) ||
            of(undefined)
          );
        }),
        takeUntil(this.progressDialogClosed$),
        filter(() => !this.preventChangingProgressDialogData),
        repeat(),
        untilDestroyed(this),
      )
      .subscribe((currentTasks) => {
        const currentTask = Array.isArray(currentTasks) ? currentTasks[0] : currentTasks;
        // AIDataGeneration has it's own custom progress dialog
        if (
          currentTask &&
          currentTask.status === 'Running' &&
          currentTask.type !== 'AIDataGeneration'
        ) {
          this.progressDialogConfig$$.next({
            description: currentTask.type,
            percent: currentTask.progress?.message ?? currentTask.progress?.percentage ?? null,
            allowCancel: true,
            taskId: currentTask.id,
          });
        } else {
          if (currentTask && currentTask.status === 'Faulted') {
            const message = generateErrorMessageFromResponse(currentTask.result as FailureResponse);
            this.notificationService.error(message);
          } else if (currentTask?.status === 'RanToCompletion' && currentTask.progress?.message) {
            if (currentTask.type === 'CsvImport') {
              const displayColumn = (currentTask.result as TableInfo).displayColumn;
              if (displayColumn) {
                const config = deepClone(this.config);
                config.displayColumn = displayColumn;
                this.updateConfig(config);
              }
              this.refetchTable(true);
            } else if (currentTask.type === 'CsvExport') {
              const { url } = currentTask.result as { url: string };
              const a = document.createElement('a');
              const filename = `${this.config.name}.csv`;
              a.setAttribute('href', url);
              a.setAttribute('download', filename || 'blaze-file');
              a.click();
              setTimeout(() => {
                URL.revokeObjectURL(url);
              }, 100);
            }
            this.notificationService.success(currentTask.progress?.message);
          }
          this.progressDialogConfig$$.next(undefined);
        }
      });
  }

  private updateConfig(config: BlazeTable | undefined): void {
    this.blazeTableService.updateConfig(config);
  }

  private initProgressDialogOnClose(): Observable<unknown> {
    if (!this.progressDialog) {
      return of(false);
    }

    return this.progressDialog.afterClosed().pipe(
      untilDestroyed(this),
      take(1),
      withLatestFrom(this.progressDialogConfig$$.pipe(first())),
      exhaustMap(([, dialogConfig]) => {
        const taskId = dialogConfig?.taskId;
        if (taskId) {
          this.preventChangingProgressDialogData = true;
          this.progressDialogConfig$$.next({
            description: 'Stopping Process',
            taskId: '',
            percent: undefined,
            allowCancel: false,
          });
          return this.jobsFacade.cancelJob(taskId).pipe(
            catchError(() => {
              // In case of an error it's possible that the task was already successfully finished
              this.refetchTable(true);

              return EMPTY;
            }),
            finalize(() => {
              this.preventChangingProgressDialogData = false;
              this.progressDialogConfig$$.next(undefined);
            }),
          );
        }
        this.progressDialogConfig$$.next(undefined);
        return of(true);
      }),
    );
  }

  private refetchTable(refreshViewportSize?: boolean): void {
    this.tablesCollService.clearCache();
    this.store
      .select(runtimeSelectors.selectProjectId)
      .pipe(
        filter(Boolean),
        first(),
        switchMap((projectId) =>
          this.tablesCollService.getWithQuery({ projectId }).pipe(
            switchMap((tables) => {
              const tableConfig = tables.find((table) => table.id === this.config.id);
              if (tableConfig) {
                const config = this.blazeTableService.syncConfigWithSchema(tableConfig);
                this.updateColumns(this.blazeTableService.prepareColumns(config));
              }

              return this.createFetchDataObservable();
            }),
          ),
        ),
      )
      .subscribe(() => {
        if (refreshViewportSize) {
          this.recheckViewportSize = true;
        }
        // Update component so the columns and rows appear in the DOM before initializing observers
        this.viewRefresh$$.next(true);
      });
  }

  private downloadPage(pageToken?: string | null): Observable<PaginatedGetResponse | undefined> {
    this.isLoading = true;
    return this.createFetchDataObservable({ nextPage: true }).pipe(
      tap((response) => {
        if (pageToken && response) {
          // Cache token of the page which was already downloaded
          this.pageTokenCache.add(pageToken);
        }
      }),
      finalize(() => {
        this.isLoading = false;
      }),
    );
  }

  private registerVirtualScroll(scrollable: CdkVirtualScrollable): void {
    this.scrollableRegistry.register(scrollable);
    this.isVirtualScrollRegistered = true;
  }

  private handleNextPageFetching(): void {
    this.nextPageFetchingSubscription?.unsubscribe();

    this.nextPageFetchingSubscription = this.virtualScrollViewports.changes
      .pipe(
        map(() => this.virtualScroll),
        filter((virtualScroll) => !!virtualScroll),
        take(1),
        switchMap((virtualScroll) => virtualScroll.renderedRangeStream),
        map(() => this.pageToken),
        filter(
          (pageToken) =>
            this.rows.length < this.totalCount &&
            !!pageToken &&
            !this.pageTokenCache.has(pageToken),
        ),
        exhaustMap((pageToken) => this.downloadPage(pageToken)),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private refreshViewportOnRowHeightChange(
    tableRowsQuery: QueryList<ElementRef<HTMLElement>>,
  ): void {
    tableRowsQuery.changes
      .pipe(
        switchMap(() => {
          const currentRows = tableRowsQuery.toArray();
          return merge(
            ...currentRows.map((row) =>
              resizeRxUtils.fromResizeObserver(row.nativeElement).pipe(
                map(() => row.nativeElement.getBoundingClientRect().height),
                distinctUntilChanged(),
              ),
            ),
          );
        }),
        debounceTime(60),
        untilDestroyed(this),
      )
      .subscribe(() => {
        // find any row and measure its width
        // this is needed to set the correct width of the footer row
        // because of its `contain:layout` style
        this.footerRowWidth = tableRowsQuery
          .toArray()
          .find(Boolean)
          ?.nativeElement.getBoundingClientRect().width;
      });

    tableRowsQuery.changes
      .pipe(
        observeOn(animationFrameScheduler),
        filter(() => tableRowsQuery.toArray().length > 0),
        take(1),
      )
      .subscribe(() => {
        this.virtualScroll?.checkViewportSize();
      });
  }

  private updateRowHeightOnCellsChange(cells: QueryList<ElementRef<HTMLElement>>): void {
    cells.changes
      .pipe(
        switchMap(() => {
          const currentCells = cells.toArray();
          return merge(
            ...currentCells.map((cell) =>
              resizeRxUtils.fromResizeObserver(cell.nativeElement).pipe(
                map(() => {
                  const height = cell.nativeElement.getBoundingClientRect().height;
                  const rowIndex = cell.nativeElement.getAttribute('aria-rowindex');
                  if (!rowIndex) {
                    return null;
                  }

                  return {
                    rowIndex,
                    height,
                  };
                }),
                distinctUntilChanged(isEqual),
                debounceTime(60),
              ),
            ),
          );
        }),
        untilDestroyed(this),
      )
      .subscribe((cellInfo) => {
        if (!cellInfo || !cellInfo.height) {
          return;
        }

        this.cellHeights[cellInfo.rowIndex] = Math.max(
          this.cellHeights[cellInfo.rowIndex] ?? 0,
          cellInfo.height,
        );

        this.virtualScrollStrategy?.updateItemSize(+cellInfo.rowIndex, cellInfo.height);
        this.virtualScroll?.checkViewportSize();
        this.cdr.markForCheck();

        runAfterFramePaint(() => this.rowSizeChanged$.next());
      });
  }

  private getRowIndex(row: Element): number {
    const indexAttr = row.getAttribute('data-index');
    return indexAttr && !isNaN(+indexAttr) ? +indexAttr : -1;
  }

  private initializeCellEdit(): void {
    this.cellEdit$$
      .pipe(
        tap((cellEditInfo) => this.logger.log('Incoming cellEditInfo:', cellEditInfo)),
        distinctUntilChanged(
          (prev, curr) =>
            prev?.rowIndex === curr?.rowIndex && prev?.column.name === curr?.column.name,
        ),
        untilDestroyed(this),
      )
      .subscribe((cellEditInfo) => {
        if (!cellEditInfo) {
          return;
        }
        this.handleCellEditInfo(cellEditInfo);
      });
  }

  private handleCellEditInfo(cellEditInfo: CellEditInfo): void {
    const textSelection = document.getSelection();
    const selectedText = textSelection?.toString();
    const selectedTextOffset =
      (textSelection && Math.min(textSelection.anchorOffset, textSelection.focusOffset)) ||
      undefined;

    this.scrollCellIntoViewWhenNeeded(cellEditInfo.cell);
    this.findPrevAndNextColForTab(cellEditInfo);

    const overlayPositionBelow = false;
    const positionStrategy = generatePositionStrategyForOverlay(
      this.overlay,
      cellEditInfo.cell,
      overlayPositionBelow,
    );

    const overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      width: cellEditInfo.cell.offsetWidth,
      height: cellEditInfo.cell.offsetHeight,
    });

    const editorComponent = new ComponentPortal(CellInputComponent);
    const componentRef = overlayRef.attach(editorComponent);

    overlayRef.updatePosition();

    this.rowSizeChanged$
      .pipe(takeUntil(overlayRef.detachments()), untilDestroyed(this))
      .subscribe(() => overlayRef.updatePosition());

    componentRef.instance.column = cellEditInfo.column;
    componentRef.instance.error = cellEditInfo.error;
    componentRef.instance.rowNum = cellEditInfo.rowIndex;
    componentRef.instance.columns = this.columns;
    componentRef.instance.projectId = this.projectId;
    componentRef.instance.valueChanged.pipe(untilDestroyed(this)).subscribe(($event) => {
      this.updateFieldValue(
        cellEditInfo.rowIndex,
        cellEditInfo.row,
        cellEditInfo.column,
        $event,
        cellEditInfo.isFooter,
      );
    });
    componentRef.instance.forceFocusAfterInit = true;
    componentRef.instance.extraStartAction = cellEditInfo.extraAction;
    componentRef.instance.parentTableCell = cellEditInfo.cell;
    componentRef.instance.value = cellEditInfo.value;
    componentRef.instance.isFirst = cellEditInfo.isFirst;
    componentRef.instance.selectedText = selectedText;
    componentRef.instance.selectedTextOffset = selectedTextOffset;
    componentRef.instance.rowSelected =
      !!this.selectedRows[cellEditInfo?.row?._id || cellEditInfo.rowIndex];

    runAfterFramePaint(() => {
      this.scrollCellIntoViewWhenNeeded(cellEditInfo.cell);
      overlayRef.updatePosition();
    });

    overlayRef
      .outsidePointerEvents()
      .pipe(untilDestroyed(componentRef.instance))
      .subscribe((event): void => {
        const target = event.target as HTMLElement;
        const instance = componentRef.instance;
        if (
          cellEditInfo.cell !== target &&
          !instance.elementRef.nativeElement.contains(target) &&
          !instance.parentTableCell?.contains(target) &&
          // when clicked on menu item and multiple then prevent closing
          (!instance.column.multipleValues ||
            (instance.column.multipleValues &&
              target.parentElement?.tagName.toLocaleLowerCase() !== 'mat-option')) &&
          // when clicked on datepicker prevent closing
          !target?.closest('mat-datepicker-content') &&
          !target.closest('.datepicker-select') &&
          // when clicked on timepicker prevent closing
          !target?.closest('.inline-editor-time-picker') &&
          // when clicked inside link panel prevent closing
          !target?.closest('blaze-find-record-panel') &&
          // when clicked on files menu prevent closing
          !target?.closest('.files-menu') &&
          // when opened paragraph dialog
          !target?.closest('be-paragraph-editor')
        ) {
          instance.focusLost.emit(true);
        }
      });

    const focusLostObserver = merge(
      intersectionRxUtils.fromIntersectionObserver(cellEditInfo.cell, { threshold: 0.9 }).pipe(
        filter((val) => !val.some((i) => i.isIntersecting)),
        map(() => undefined),
      ),
      intersectionRxUtils
        .fromIntersectionObserver(cellEditInfo.cell, {
          threshold: 0.9,
          root: cellEditInfo.cell.closest('.blaze-virtual-scrollable'),
        })
        .pipe(
          filter((val) => !val.some((i) => i.isIntersecting)),
          map(() => undefined),
        ),
      componentRef.instance.focusLost,
      this.componentDestroy$.asObservable().pipe(map(() => undefined)),
    );
    focusLostObserver.pipe(take(1), untilDestroyed(this)).subscribe(() => {
      overlayRef?.detach();
      overlayRef?.dispose();
      destroyOverlayRef(overlayRef, cellEditInfo, this.cellEdit$$);
    });

    resizeRxUtils
      .fromResizeObserver(cellEditInfo.cell)
      .pipe(takeUntil(focusLostObserver), untilDestroyed(this))
      .subscribe(() => {
        overlayRef.updateSize({
          width: cellEditInfo.cell.offsetWidth,
          height: cellEditInfo.cell.offsetHeight,
        });
      });
  }

  private async scrollCellIntoViewWhenNeeded(
    cell: (typeof this.siblingColsForActiveCell)['next'] | HTMLElement,
  ): Promise<void> {
    if (!cell) {
      return;
    }

    if (cell instanceof HTMLElement) {
      cell = {
        column: String(cell.getAttribute('data-column')),
        rowIndex: Number(cell.getAttribute('aria-rowindex')),
      };
    }

    if ('column' in cell && 'rowIndex' in cell) {
      const elRef = this.elRef.nativeElement;
      const headerElement = elRef.querySelector(`th[data-columnName="${cell.column}"]`);
      const isFirstHeaderElement = !headerElement.previousElementSibling;
      const headerElementRect = headerElement.getBoundingClientRect();
      const footerElementRect = this.addRowTpl?.nativeElement?.getBoundingClientRect();

      let scrollContainer = headerElement.closest('[blazevirtualscrollableelement]');
      const containerRect = scrollContainer?.getBoundingClientRect();

      const headerElementWidth = headerElementRect.width;
      const isLeftInView = headerElementRect.left - headerElementWidth >= containerRect.left;
      const isRightInView = headerElementRect.right <= containerRect.right;

      const isHorizontallyThInView =
        headerElementRect && containerRect && isLeftInView && isRightInView;

      if (headerElementRect && containerRect && !isHorizontallyThInView) {
        let scrollValue = 0;
        // move all the way to the left
        if (isFirstHeaderElement) {
          scrollValue = (footerElementRect?.left || 0) - headerElementWidth;
        } else if (!isLeftInView) {
          scrollValue = headerElementRect.left - containerRect.left - headerElementWidth;
        } else if (!isRightInView) {
          scrollValue = headerElementRect.right - containerRect.right;
        }
        scrollContainer?.scrollBy(scrollValue, 0);
      }

      const stringSelector = `td[data-column="${cell.column}"][aria-rowindex="${cell.rowIndex}"]`;
      await this.waitForCell(stringSelector);
      const cellElement = elRef.querySelector(stringSelector);
      const elementRect = cellElement.getBoundingClientRect();

      scrollContainer = cellElement.closest('[blazevirtualscrollableelement]');

      const viewportWidth = scrollContainer.clientWidth;
      const viewportHeight = scrollContainer.clientHeight;

      const isVisibleFromTop = elementRect.top >= containerRect.top;
      // minus height to check if hidden by 'Add new row' row
      // ignore when focused 'Add new row' and it is sticky to the bottom
      const isVisibleFromBottom =
        this.rows.length < this.totalCount
          ? true
          : elementRect.bottom <=
            containerRect.bottom - (footerElementRect ?? headerElementRect)?.height;

      const isVerticallyInView =
        // element visible in container
        isVisibleFromTop &&
        isVisibleFromBottom &&
        // element visible on the screen
        elementRect.top >= 0 &&
        elementRect.bottom <= viewportHeight;
      const isHorizontallyInView =
        //element visible in container
        elementRect.left >= containerRect.left &&
        elementRect.right <= containerRect.right &&
        //element visible on the screen
        elementRect.left >= 0 &&
        elementRect.right <= viewportWidth;

      if (!isVerticallyInView || !isHorizontallyInView) {
        if (!isVisibleFromTop) {
          scrollContainer.scrollBy({
            top: elementRect.top - containerRect.top - headerElementRect.height,
            behavior: 'auto',
          });
        } else if (!isVisibleFromBottom) {
          // 2px for border
          scrollContainer.scrollBy({
            top:
              elementRect.bottom -
              containerRect.bottom +
              (footerElementRect ?? headerElementRect).height +
              2,
            behavior: 'auto',
          });
        } else {
          headerElement.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
        }
      }
    }
  }

  private async waitForCell(stringSelector: string): Promise<HTMLElement> {
    return new Promise((resolve) => {
      const cell: HTMLElement | null = document.querySelector(stringSelector);
      if (cell) {
        return resolve(cell);
      }

      const observer = new MutationObserver(() => {
        const cell: HTMLElement | null = document.querySelector(stringSelector);
        if (cell) {
          resolve(cell);
          observer.disconnect();
        }
      });

      observer.observe(document.body, {
        subtree: true,
        childList: true,
      });
    });
  }

  private isSystemTableUsersAccounts(): boolean {
    return (
      (this.config.systemTableType &&
        [SystemTableType.Users, SystemTableType.Accounts].includes(this.config.systemTableType)) ||
      false
    );
  }

  private allowNewRowException(column: DatasetColumn, rowIndex: number): boolean {
    if (rowIndex === this.rows.length - 1) {
      return !column.isComputed && !column.isInternal && this.isSystemTableUsersAccounts();
    }
    return false;
  }

  /**
   * check if column is editable
   * @param column = DatasetColumn | string (name or id)
   */
  private isColumnEditable(column: DatasetColumn | string): boolean {
    if (typeof column === 'string') {
      const columnRef = this.columns.find((col) => col.id === column || col.name === column);
      if (!columnRef) {
        return false;
      }
      column = columnRef;
    }
    return (
      !this.config.isChangeTrackingTable &&
      !column.isComputed &&
      !column.isInternal &&
      (!column.isProtected || (column.isProtected && this.isSystemTableUsersAccounts()))
    );
  }

  private initializeActions(): void {
    this.blazeTableStore.updateRecords$
      .pipe(distinctUntilChanged(isDeepEqual), untilDestroyed(this))
      .subscribe((updateRecords) => {
        const { config, column } = updateRecords || {};
        if (!config || !column) {
          return;
        }
        this.refreshSchemaAfterClose = true;
        this.updateTableConfig(config)
          .pipe(this.countAction(), tap({ error: this.showError() }))
          .subscribe(() => this.updateRowsAndColumnsAfterSuccessDelete(config, column));
      });

    this.blazeTableStore.deleteColumn$.pipe(untilDestroyed(this)).subscribe((colId) => {
      if (!colId) {
        return;
      }
      this.refreshSchemaAfterClose = true;
      const sourceColumn = this.config.columns.find((c) => c.id === colId);
      if (!sourceColumn) {
        this.logger.error('Action: deleteColumn re-run, missing sourceColumn');
        return;
      }
      const action: BlazeTableAction = {
        sourceColumn,
        type: BlazeTableActionType.Delete,
      };
      this.blazeTableStore.clearDeleteColumnRequest();
      this.execColumnAction(action).subscribe();
    });
  }

  private initializeActionsInProgressTracking(): void {
    this.actionsInProgress$ = combineLatest({
      actions: this.blazeTableStore.actionsInProgress$.pipe(map((count) => count > 0)),
      cellOpened: this.cellEdit$$.pipe(
        debounceTime(500),
        map((cellEdit) => !!cellEdit?.cell),
      ),
    }).pipe(
      map(({ actions, cellOpened }) => actions || cellOpened),
      distinctUntilChanged(),
      untilDestroyed(this),
    );
  }
}
